import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { useAfterMount } from '@yandex-360/orb-components/dist/hooks/useAfterMount';
import { LoggableError } from '@yandex-int/messenger.errors';
import { useSelector } from 'react-redux';

import { getAudioContext } from '../../../helpers/audio-context';
import { AudioHandler, getDiagnosticAudioLevelAnalyzer, States } from '../../../helpers/audio-helper';
import { createLogger } from '../../../helpers/loggerFacade';
import { useActions } from '../../../hooks';
import { getAudioOutputDeviceSelector, setIsAudioOutputDiagnosticRunAction } from '../../settings';

const logger = createLogger('use-output-audio-handler');

const audioContext = getAudioContext();
const diagnosticAnalyzer = getDiagnosticAudioLevelAnalyzer(audioContext);
export const OutputAudioHandlerContext = createContext<{
    audioHandlerRef?: React.MutableRefObject<AudioHandler | null>;
    audioHandlerState?: States | null;
}>({});
export const useOutputAudioHandlerContext = () => useContext(OutputAudioHandlerContext);
export const useOutputAudioHandler = () => {
    const { setIsAudioOutputDiagnosticRun } = useActions({
        setIsAudioOutputDiagnosticRun: setIsAudioOutputDiagnosticRunAction,
    });
    const currentOutputDeviceId = useSelector(getAudioOutputDeviceSelector)?.deviceId;
    const audioHandlerRef = useRef<AudioHandler | null>(null);
    const [audioHandlerState, setAudioHandlerState] = useState<States | null>(null);

    useEffect(() => {
        if (!audioContext || !diagnosticAnalyzer) {
            return;
        }

        if (!audioHandlerRef.current) {
            audioHandlerRef.current = new AudioHandler(audioContext, diagnosticAnalyzer);
        }

        const onPlayStop = () => {
            setIsAudioOutputDiagnosticRun(false);
        };

        audioHandlerRef.current.onStateChanged.addListener(setAudioHandlerState);
        audioHandlerRef.current.onPlayStop.addListener(onPlayStop);

        setAudioHandlerState(audioHandlerRef.current.state);
        return () => {
            audioHandlerRef.current?.onStateChanged.removeListener(setAudioHandlerState);
            audioHandlerRef.current?.onPlayStop.removeListener(onPlayStop);
        };
    }, [setIsAudioOutputDiagnosticRun]);

    useAfterMount(() => {
        const deviceId = currentOutputDeviceId ?? '';
        audioHandlerRef.current?.changeOutputDevice(deviceId).catch((error) => {
            logger.error(error, {
                method: 'changeOutputDevice',
                additional: {
                    deviceId: deviceId,
                },
            });
            if (!audioHandlerRef.current) {
                logger.warn(new Error('AudioHandler is not defined') as LoggableError);
                return;
            }
            setIsAudioOutputDiagnosticRun(false);
            audioHandlerRef.current.emergencyStop();
        });
    }, [currentOutputDeviceId]);
    useEffect(() => {
        return () => {
            // unmount component
            setIsAudioOutputDiagnosticRun(false);
            audioHandlerRef.current?.destroy();
        };
    }, [setIsAudioOutputDiagnosticRun]);
    return {
        audioHandlerRef,
        audioHandlerState,
    };
};
